import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Form } from '../../../components/Form';
import { FormField } from '../../../components/FormField';
import { Input } from '../../../components/Input';
import Button from '../../../components/Button/Button';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { useAddNewStore } from '../../../network';
import Loading from '../../../components/Loading';
import { timezoneList } from '../../../utils/utils';
import { CustomSelect } from '../../../components/Shared/CustomSelect';
import { GeoAddressSelect } from '../GeoAddressSelect';

export const AddStorePage = () => {
    const { t } = useTranslation();

    const { mutate: addNewStore, isLoading } = useAddNewStore();

    const validationSchema = yup.object().shape({
        name: yup.string().required(t('Required!')),
        address: yup.string().required(t('Required!')),
        time_zone_name: yup.string().required(t('Required!')),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            address: '',
            time_zone_name: '',
            comment: '',
            service_charge_percent: '',
            postCode: '',
            country: '',
            phoneCode: '',
            phoneNumber: '',
            lng: '',
            lat: '',
        },
        validateOnChange: false,
        validateOnBlur: false,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            addNewStore({ params: values });
        },
    });

    const { handleChange, handleSubmit, setFieldValue, errors } = formik;
    const { name, email, address, phoneCode, phoneNumber, postCode, country, time_zone_name } = formik.values;

    const timezones = Object.keys(timezoneList).map((time) => ({ label: timezoneList[time], value: time }));

    const pages = [
        { name: t('Stores'), path: '/chain/stores' },
        {
            name: t('Add store'),
            path: '/chain/stores/create',
        },
    ];

    return (
        <Loading loading={isLoading}>
            <Breadcrumbs pages={pages} />

            <div className=" max-w-screen-sm rounded-xl shadow-lg flex items-center space-x-4 p-4 sm:p-4">
                <Form handleSubmit={handleSubmit} className="w-full">
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <FormField label={t('Name')} htmlFor="name" className="col-span-6" required error={errors.name}>
                            <Input id="name" type="text" name="name" onChange={handleChange} value={name} placeholder={t('Store name')} />
                        </FormField>
                        <FormField label={t('Address')} className="col-span-6" htmlFor="address" required error={errors.address}>
                            <GeoAddressSelect setFieldValue={setFieldValue} address={address}/>
                        </FormField>
                        <FormField label={t('Timezone')} className="col-span-6" htmlFor="time_zone_name" required error={errors.time_zone_name}>
                            <CustomSelect
                                isDisabled={isLoading}
                                name="time_zone_name"
                                options={timezones}
                                className="w-full mt-1"
                                onChange={({ value }) => setFieldValue('time_zone_name', value)}
                                defaultValue={time_zone_name}
                                placeholder={t('Select timezone')}
                            />
                        </FormField>
                        <FormField disabled popoverText="On development stage" label={t('Email')} htmlFor="email" className="col-span-6">
                            <Input id="email" type="text" name="email" onChange={handleChange} value={email} placeholder="you@example.com" />
                        </FormField>
                        <FormField disabled popoverText="On development stage" label={t('Phone')} className="col-span-6 sm:col-start-1 sm:col-end-3" htmlFor="phoneCode">
                            <Input id="phoneCode" type="text" name="phoneCode" onChange={handleChange} value={phoneCode} placeholder={`${t('Country Code')} (+44)`} />
                        </FormField>
                        <FormField disabled popoverText="On development stage" className="col-span-6 sm:col-start-3 sm:col-end-7">
                            <Input id="phoneNumber" type="text" name="phoneNumber" onChange={handleChange} value={phoneNumber} placeholder={t('Phone')} />
                        </FormField>
                        <FormField disabled popoverText="On development stage" className="col-span-6 sm:col-span-3">
                            <Input id="postCode" type="text" name="postCode" onChange={handleChange} value={postCode} placeholder={t('Post Code')} />
                        </FormField>
                        <FormField disabled popoverText="On development stage" className="col-span-6 sm:col-span-3">
                            <Input type="text" name="country" onChange={handleChange} value={country} placeholder={t('Country')} />
                        </FormField>
                    </div>
                    <Button
                        type="submit"
                        isUpperCase={false}
                        isLoading={isLoading}
                        disabled={isLoading}
                        className="ml-auto h-9 flex justify-center py-1.5 px-6 ml-2 mt-7 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-gray-900 hover:bg-gray-700 focus:outline-none"
                    >
                        {t('Add')}
                    </Button>
                </Form>
            </div>
        </Loading>
    );
};
